/**
 * Lab Timer Component - Track lab completion time
 */
import React, { useState, useEffect } from 'react';
import { Clock, Play, Square } from 'lucide-react';
import { apiService } from '../services/api';

const LabTimer = ({ course, labId, onTimerStart, onLabComplete }) => {
  const [timerState, setTimerState] = useState({
    isActive: false,
    elapsedSeconds: 0,
    startTime: null,
    isCompleted: false
  });

  const [loading, setLoading] = useState(false);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update timer every second
  useEffect(() => {
    let interval = null;
    if (timerState.isActive && timerState.startTime) {
      interval = setInterval(() => {
        const now = new Date().getTime();
        const elapsed = Math.floor((now - timerState.startTime) / 1000);
        setTimerState(prev => ({
          ...prev,
          elapsedSeconds: elapsed
        }));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerState.isActive, timerState.startTime]);

  // Load existing timer state on component mount
  useEffect(() => {
    const checkExistingTimer = async () => {
      if (!course || !labId) return;
      
      try {
        const response = await apiService.getLabTimerStatus(course, labId);
        if (response.success && response.data.timer_active) {
          const startTime = new Date(response.data.start_time).getTime();
          setTimerState({
            isActive: true,
            startTime: startTime,
            elapsedSeconds: response.data.elapsed_seconds || 0,
            isCompleted: false
          });
        }
      } catch (error) {
        // No existing timer, start fresh
        console.log('No existing timer found');
      }
    };

    checkExistingTimer();
  }, [course, labId]);

  // Start timer function
  const handleStartTimer = async () => {
    setLoading(true);
    try {
      const response = await apiService.startLabTimer(course, labId);
      const startTime = new Date().getTime();
      
      setTimerState({
        isActive: true,
        startTime: startTime,
        elapsedSeconds: 0,
        isCompleted: false
      });

      // Store in localStorage for persistence
      localStorage.setItem('lab_timer_start', startTime.toString());
      localStorage.setItem('lab_timer_course', course);
      localStorage.setItem('lab_timer_labId', labId);
      
      if (onTimerStart) onTimerStart();
    } catch (error) {
      console.error('Failed to start timer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stop timer function
  const handleStopTimer = async () => {
    setLoading(true);
    try {
      const response = await apiService.completeLabTimer(course, labId);
      
      setTimerState(prev => ({
        ...prev,
        isActive: false,
        isCompleted: true
      }));

      // Clear localStorage
      localStorage.removeItem('lab_timer_start');
      localStorage.removeItem('lab_timer_course');
      localStorage.removeItem('lab_timer_labId');
      
      if (onLabComplete) onLabComplete(response);
    } catch (error) {
      console.error('Failed to stop timer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Render nothing if no course/labId
  if (!course || !labId) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-emerald-600/20 to-emerald-500/20 border border-emerald-400/50 rounded-xl p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-emerald-400" />
          <h3 className="text-emerald-300 font-semibold">Lab Timer</h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono font-bold text-white">
            {formatTime(timerState.elapsedSeconds)}
          </div>
          <div className="text-xs text-emerald-300">
            {timerState.isActive ? 'Running' : timerState.isCompleted ? 'Completed' : 'Ready'}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-300">
          <div>Course: <span className="text-emerald-300 font-medium">{course}</span></div>
          <div>Lab: <span className="text-emerald-300 font-medium">{labId}</span></div>
        </div>

        <div className="flex gap-2">
          {!timerState.isActive ? (
            <button
              onClick={handleStartTimer}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Start
            </button>
          ) : (
            <button
              onClick={handleStopTimer}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              Stop
            </button>
          )}
        </div>
      </div>

      {timerState.isCompleted && (
        <div className="mt-3 p-2 bg-green-500/20 border border-green-400/50 rounded text-green-300 text-sm">
          ✅ Lab completed in {formatTime(timerState.elapsedSeconds)}
        </div>
      )}
    </div>
  );
};

export default LabTimer;
